import { Controller } from "@hotwired/stimulus"

// The edit-release form's live behavior: Track to Playlist applies only to
// multi-track releases (album, ep, mixtape; a single IS the track). The
// wrap is HIDDEN, never disabled, so an existing value survives a type
// flip round trip without silent erasure (committee spec).
//
// Effective campaign date (owner directive 2026-09-09): when the release
// date is more than staleDays in the past and no campaign date is set, a
// native modal asks ONCE per form whether the campaign starts on another
// day. Keep stores the release date into the field (so the edit page never
// re-asks); Use stores the typed date and reveals the field. Chrome fires
// change on every complete date while a year is typed digit by digit
// (0002, 0020, 0202 are all "old"), so the change is debounced, years
// before 2000 are ignored, and focusout evaluates at once. A submit that
// outruns the debounce is caught and resumed after the answer.
//
// A music video is exempt from the weekly check-in (Ticket #1222, owner
// rulings 2026-10-05): the three check-in selects (Spotify for Artists
// status and pitch, DistroKid) lock, disabled so nothing submits and the
// stored answers survive the save, on their blank option relabeled "Not
// needed". ONE line says why: the section line swaps, and the pitch and
// DistroKid fine print hide while locked. Each locked select keeps what it
// held in data-held-answer (the server writes it when the page opens on a
// music video), so switching the type away puts those values back, with
// the blank option reading "Not answered yet" again.
export default class extends Controller {
  static targets = ["type", "trackWrap", "pitchedSelect", "pitchedOther",
                    "s4aStatus", "s4aPitched", "pitchedHelp", "distrokid", "dkHelp",
                    "checkInLine", "exemptLine",
                    "status", "hiddenBox",
                    "releaseDate", "campaignWrap", "campaignDate", "campaignOpener",
                    "campaignModal", "modalDate"]
  static values = { multiTrack: Array, checkInExempt: Array, exemptBlank: String, unansweredBlank: String,
                    staleDays: { type: Number, default: 7 } }

  connect() {
    this.toggle()
    this.s4aChanged()
    this.statusChanged()
    this.asked = false
    this.pendingSubmit = false
    if (this.hasReleaseDateTarget) {
      this.lastReleaseDate = this.releaseDateTarget.value
      this.submitHandler = (e) => this.guardSubmit(e)
      this.form()?.addEventListener("submit", this.submitHandler)
      // The edit page: an old release with the question never asked.
      if (this.hasStatusTarget && this.statusTarget.value !== "completed") this.maybeAsk()
    }
  }

  disconnect() {
    clearTimeout(this.dateTimer)
    this.form()?.removeEventListener("submit", this.submitHandler)
    if (this.hasCampaignModalTarget) this.campaignModalTarget.removeEventListener("keydown", this.trapHandler)
  }

  // Hidden applies to active and upcoming releases only (the model clears
  // it otherwise); the box disables rather than silently unticking on save.
  statusChanged() {
    if (!this.hasStatusTarget || !this.hasHiddenBoxTarget) return
    this.hiddenBoxTarget.disabled = !["active", "upcoming"].includes(this.statusTarget.value)
  }

  // Pitched-via-S4A is meaningless until the release is uploaded; the
  // control disables (a disabled select never submits, so the stored value
  // is left untouched rather than clobbered).
  s4aChanged() {
    if (!this.hasS4aStatusTarget || !this.hasS4aPitchedTarget) return
    this.s4aPitchedTarget.disabled = this.s4aStatusTarget.value !== "uploaded"
  }

  toggle() {
    const show = this.multiTrackValue.includes(this.typeTarget.value)
    this.trackWrapTarget.hidden = !show
    this.checkInExemptChanged()
  }

  // Lock or unlock the three check-in questions for the chosen type.
  // Idempotent: data-held-answer on the status select says whether they are
  // locked now, so connect() on a page that opened locked is a no-op.
  checkInExemptChanged() {
    if (!this.hasS4aStatusTarget || !this.hasS4aPitchedTarget) return
    const exempt = this.checkInExemptValue.includes(this.typeTarget.value)
    const locked = "heldAnswer" in this.s4aStatusTarget.dataset
    const selects = [this.s4aStatusTarget, this.s4aPitchedTarget, ...this.distrokidTargets]
    if (exempt && !locked) {
      selects.forEach((select) => {
        select.dataset.heldAnswer = select.value
        select.value = ""
        select.disabled = true
        this.labelBlank(select, this.exemptBlankValue)
      })
    } else if (!exempt && locked) {
      selects.forEach((select) => {
        select.value = select.dataset.heldAnswer
        delete select.dataset.heldAnswer
        select.disabled = false
        this.labelBlank(select, this.unansweredBlankValue)
      })
      // Back to the ordinary rule: pitched waits for Uploaded.
      this.s4aChanged()
    }
    if (this.hasCheckInLineTarget) this.checkInLineTarget.hidden = exempt
    if (this.hasExemptLineTarget) this.exemptLineTarget.hidden = !exempt
    if (this.hasPitchedHelpTarget) this.pitchedHelpTarget.hidden = exempt
    if (this.hasDkHelpTarget) this.dkHelpTarget.hidden = exempt
  }

  // The blank option's words: "Not needed" while locked, "Not answered
  // yet" otherwise (both handed in from ReleasesBoardHelper).
  labelBlank(select, text) {
    const blank = select.querySelector('option[value=""]')
    if (blank && text) blank.textContent = text
  }

  // "Type a name...": the text input takes over the ONE pitched_by param;
  // the select is disabled so it never submits alongside it.
  pitchedChanged() {
    if (!this.hasPitchedSelectTarget) return
    if (this.pitchedSelectTarget.value !== "__other__") return
    this.pitchedSelectTarget.disabled = true
    this.pitchedSelectTarget.hidden = true
    this.pitchedOtherTarget.disabled = false
    this.pitchedOtherTarget.hidden = false
    this.pitchedOtherTarget.focus()
  }

  // --- Effective campaign date ---

  dateChanged() {
    this.followReleaseDate()
    clearTimeout(this.dateTimer)
    this.dateTimer = setTimeout(() => this.maybeAsk(), 600)
  }

  dateSettled() {
    clearTimeout(this.dateTimer)
    this.maybeAsk()
  }

  // "Same as the release" follows a re-dated release out (the server does
  // the same on save): the question is fresh for the new date.
  followReleaseDate() {
    if (!this.hasCampaignDateTarget) return
    const now = this.releaseDateTarget.value
    if (now === this.lastReleaseDate) return
    if (this.campaignDateTarget.value && this.campaignDateTarget.value === this.lastReleaseDate) {
      this.campaignDateTarget.value = ""
      this.asked = false
    }
    this.lastReleaseDate = now
  }

  stale() {
    const value = this.releaseDateTarget.value
    if (!value) return false
    const day = new Date(value + "T00:00:00")
    if (isNaN(day) || day.getFullYear() < 2000) return false
    const today = new Date(); today.setHours(0, 0, 0, 0)
    return (today - day) / 86400000 > this.staleDaysValue
  }

  campaignBlank() {
    return !this.hasCampaignDateTarget || !this.campaignDateTarget.value
  }

  maybeAsk() {
    if (this.asked || !this.hasCampaignModalTarget) return
    if (!this.stale() || !this.campaignBlank()) return
    this.openModal()
  }

  guardSubmit(event) {
    if (this.asked || !this.hasCampaignModalTarget) return
    if (!this.stale() || !this.campaignBlank()) return
    event.preventDefault()
    this.pendingSubmit = true
    this.openModal()
  }

  openModal() {
    this.asked = true
    const today = new Date()
    const iso = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`
    this.modalDateTarget.value = iso
    this.modalDateTarget.min = this.releaseDateTarget.value || ""
    this.campaignModalTarget.hidden = false
    this.trapHandler = (e) => this.trapFocus(e)
    this.campaignModalTarget.addEventListener("keydown", this.trapHandler)
    this.modalDateTarget.focus()
  }

  closeModal() {
    this.campaignModalTarget.hidden = true
    this.campaignModalTarget.removeEventListener("keydown", this.trapHandler)
    if (this.pendingSubmit) {
      this.pendingSubmit = false
      this.form()?.requestSubmit()
    }
  }

  keepReleaseDate() {
    this.campaignDateTarget.value = this.releaseDateTarget.value
    this.closeModal()
  }

  useCampaignDate() {
    const value = this.modalDateTarget.value
    if (!value) return this.modalDateTarget.focus()
    this.campaignDateTarget.value = value
    this.showCampaign()
    this.closeModal()
  }

  // Enter inside the modal's date input must not submit the release form
  // around it; it is the primary answer.
  modalKeydown(event) {
    if (event.key !== "Enter") return
    event.preventDefault()
    this.useCampaignDate()
  }

  openCampaign() {
    this.asked = true
    this.showCampaign()
    this.campaignDateTarget.focus()
  }

  showCampaign() {
    this.campaignWrapTarget.hidden = false
    if (this.hasCampaignOpenerTarget) this.campaignOpenerTarget.hidden = true
  }

  clearCampaign() {
    this.campaignDateTarget.value = ""
    this.asked = true
    this.campaignWrapTarget.hidden = true
    if (this.hasCampaignOpenerTarget) this.campaignOpenerTarget.hidden = false
  }

  trapFocus(event) {
    if (event.key === "Escape") { event.preventDefault(); return this.keepReleaseDate() }
    if (event.key !== "Tab") return
    const stops = Array.from(this.campaignModalTarget.querySelectorAll("input, button"))
    const first = stops[0]
    const last = stops[stops.length - 1]
    if (!stops.includes(document.activeElement)) { event.preventDefault(); return first.focus() }
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus() }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus() }
  }

  form() {
    return this.element.closest("form")
  }
}
